/**
 * @comment Aaron: This was implemented by myself but updated by Ben at some point. As
 * usual Ben left no comments to make the logic easy to follow. I have commented this as
 * best I can in the time given, to make it easier to understand. Could use a minor
 * refactor to improve code flow and logic.
 */

import { useCallback, useContext, useState, useRef, useEffect } from "react";

import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { CaptureContext } from "../../../context/domain/planning/CaptureContext"; // Adjust the import path as necessary
import {
  getUserDisplayName,
  getUserInitials,
  groupScopesByDay,
  mapScopesByDate,
} from "../../../utils/business";

import EditWorkScopes from "./EditWorkScopes";
import WorkScopesSummary from "./WorkScopesSummary";

import { AddressMapsLink, Button, IconText } from "../../../components/ui";

/**
 * Job component to display and edit job details and scopes.
 * @param {Object} props - Component props.
 * @param {Object} props.job - Job object containing job details.
 * @param {Array} props.scopes - Array of scope objects related to the job.
 * @returns {JSX.Element} - Rendered Job component.
 */
const Job = ({ job, scopes }) => {
  const { showWeekends, datesOfWeek, reloadScopes } = useContext(WeeklyContext);
  const { isReadOnly, detailsOnly } = useContext(DispatchContext);
  // This is to set options that should ONLY be set when capturing. E.G. setting all
  //  jobs to editing mode
  const captureContext = useContext(CaptureContext);
  // We need to get captureComponent like this as captureContext is not always available
  var captureComponent = false;
  if (captureContext) {
    captureComponent = captureContext.captureComponent;
  }

  const scopesByDay = groupScopesByDay(scopes);
  const shiftedWeek = mapScopesByDate(datesOfWeek, scopesByDay);
  const emptyJob = scopesByDay.size === 0 ? true : false;

  // This edit state is per job so resides at this component level.
  // detailsOnly embeds start (and stay) in details/edit view.
  const [isEditing, setIsEditing] = useState(Boolean(detailsOnly));

  /**
   * Hovered Schedule-block link as `{ id, colorHex }`, or null. Set while a
   * scope card with a linked schedule block is hovered; all cards in this job
   * row sharing that block id highlight with the shared color. Per-job state
   * (not context) keeps hover re-renders local to the row.
   */
  const [hoveredBlock, setHoveredBlock] = useState(null);

  const [isCopyMode, setIsCopyMode] = useState(false);
  const [copyModeScope, setCopyModeScope] = useState(null);
  /**
   * Enter copy-scope mode using the given source scope.
   *
   * @param {Object} scope WorkScopeAspect to copy from.
   * @returns {void}
   */
  const handleStartCopy = useCallback((scope) => {
    setIsCopyMode(true);
    setCopyModeScope(scope);
  }, []);
  /**
   * Exit copy-scope mode and clear the source scope.
   *
   * @param {void}
   * @returns {void}
   */
  const handleEndCopy = useCallback(() => {
    setIsCopyMode(false);
    setCopyModeScope(null);
  }, []);

  /**
   * Toggle summary vs details view and reload scopes so out-of-edit changes appear.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handleToggleEdit = useCallback(async () => {
    if (detailsOnly) {
      return;
    }
    setIsEditing((prev) => !prev);
    await reloadScopes(); // Reload as changes are actually possible outside of edit mode
  }, [detailsOnly, reloadScopes]);

  const buttonRef = useRef(null);
  const managerRef = useRef(null);
  const [buttonText, setButtonText] = useState(
    isEditing ? "Switch to summary view" : "Switch to details view"
  );
  const [managerText, setManagerText] = useState("");

  // Below this is the logic to update the button text and manager text based on the
  // width of the containing div. This may prove usefule in other components. If so,
  // we could consider making this an independent hook later.

  // This effect is to update the button text based on the width of the button
  useEffect(() => {
    /**
     * Shrink the edit/summary button label when the job header is narrow.
     *
     * @param {void}
     * @returns {void}
     */
    const updateButtonText = () => {
      if (buttonRef.current) {
        const buttonWidth = buttonRef.current.offsetWidth;
        if (isReadOnly) {
          if (buttonWidth < 134) {
            setButtonText(isEditing ? "Summary" : "Details");
          } else {
            setButtonText(
              isEditing ? "Switch to Summary View" : "Switch to Detail View"
            );
          }
          return;
        }
        if (buttonWidth < 78) {
          setButtonText(isEditing ? "Summary" : "Edit");
        } else if (buttonWidth < 134) {
          setButtonText(isEditing ? "View Summary" : "View Edit");
        } else {
          setButtonText(
            isEditing ? "Switch to Summary View" : "Switch to Details View"
          );
        }
      }
    };
    updateButtonText();
    window.addEventListener("resize", updateButtonText);
    return () => {
      window.removeEventListener("resize", updateButtonText);
    };
  }, [isEditing, buttonText, isReadOnly]);

  // Calculate the total length of all manager names in increments for compact display.
  const jobManagers = job["managers"];
  const totalManagerNameLength = jobManagers.reduce((total, manager) => {
    return (
      total +
      (manager?.first?.length ?? 0) +
      (manager?.last?.length ?? 0)
    );
  }, 0);
  const totalManagerFirstNames = jobManagers.reduce((total, manager) => {
    return total + (manager?.first?.length ?? 0);
  }, 0);
  // This effect is to update the manager text based on the width of the containing div.
  useEffect(() => {
    /**
     * Compact manager names (initials → first names → full) to fit the header width.
     *
     * @param {void}
     * @returns {void}
     */
    const updateManagerText = () => {
      if (managerRef.current) {
        const managerWidth = managerRef.current.offsetWidth;
        if (managerWidth < totalManagerFirstNames * 11) {
          setManagerText(
            job["managers"]
              ?.map((manager) =>
                getUserInitials(manager, {
                  fallback: "",
                  useEmailFallback: false,
                })
              )
              .join(", ") || "None"
          );
        } else if (managerWidth < totalManagerNameLength * 10 + 10) {
          setManagerText(
            job["managers"]?.map((manager) => manager?.first ?? "").join(", ")
          );
        } else {
          setManagerText(
            job["managers"]
              ?.map((manager) => getUserDisplayName(manager))
              .join(", ")
          );
        }
        if (job["managers"].length === 0) {
          if (managerWidth < 110) {
            setManagerText("None");
          } else {
            setManagerText("No managers");
          }
        }
      }
    };
    updateManagerText();
    window.addEventListener("resize", updateManagerText);
    return () => {
      window.removeEventListener("resize", updateManagerText);
    };
  }, [job, jobManagers, totalManagerFirstNames, totalManagerNameLength]);

  return (
    <div
      className={`flex w-full h-100 mb-2 justify-center rounded-md bg-gray-200 ${
        emptyJob && !isEditing && !detailsOnly
          ? "opacity-30 hover:opacity-100 cursor-pointer"
          : ""
      }`}
      onClick={
        !isEditing && emptyJob && !detailsOnly
          ? () => setIsEditing(true)
          : undefined
      }
    >
      <div className="w-full h-full flex flex-col">
        <div
          onClick={detailsOnly ? undefined : handleToggleEdit}
          className={`text-md bg-primary-300 shadow-md w-full h-3/20 flex items-center justify-between rounded-t-md p-0 pl-2 ${
            detailsOnly ? "" : "cursor-pointer"
          }`}
        >
          <div
            className={`grid w-full ${
              detailsOnly
                ? "grid-cols-[30%_70%] lg:grid-cols-[28%_72%]"
                : "grid-cols-[25%_60%_15%] lg:grid-cols-[25%_55%_20%]"
            }`}
          >
            <div className="flex items-center text-gray-300">
              <p className="font-bold text-white rounded-3xl px-1 truncate">
                {job.number} - {job.description}
              </p>
            </div>
            <div className="grid sm:grid-cols-[71px_142px_auto] grid-cols-[46px_76px_auto] gap-1 text-gray-300 max-w-full">
              <IconText
                icon="time"
                text={job.default_time?.slice(0, -3) || "No time set"}
                classes="border-[1.5px] my-[6px] border-gray-400 rounded-3xl max-h-[36px] truncate"
              />
              <div ref={managerRef} className="flex items-center ml-1">
                <IconText
                  icon="clipboard"
                  text={managerText}
                  classes="border-[1.5px] my-[6px] border-gray-400 rounded-3xl max-h-[36px] truncate"
                />
              </div>
              <div className="flex items-center truncate">
                <AddressMapsLink
                  address={job["default_address"]?.address}
                  className="flex items-center truncate"
                >
                  <IconText
                    icon="location"
                    text={job["default_address"]?.address || "No address set"}
                    truncate={true}
                    classes="border-[1.5px] my-[6px] border-gray-400 rounded-3xl max-h-[36px] truncate"
                  />
                </AddressMapsLink>
              </div>
            </div>
            {!detailsOnly ? (
              <Button
                ref={buttonRef}
                variant="none"
                text={buttonText}
                className="text-green-500 my-auto mr-3 text-right truncate pointer-events-auto dont-block"
              />
            ) : null}
          </div>
        </div>
        <div className="w-full flex flex-row justify-start">
          <div
            className={`grid ${showWeekends ? "grid-cols-7" : "grid-cols-5"} gap-2 p-2 pt-2 w-full h-full`}
          >
            {shiftedWeek.map(([date, scopes], i) => (
              <div key={i} className="flex flex-col">
                {isEditing || captureComponent ? (
                  <EditWorkScopes
                    dayScopes={scopes}
                    job={job}
                    date={date}
                    hoveredBlock={hoveredBlock}
                    onBlockHover={setHoveredBlock}
                    isCopyMode={isCopyMode}
                    copyModeScope={copyModeScope}
                    onStartCopy={handleStartCopy}
                    onEndCopy={handleEndCopy}
                  />
                ) : (
                  <WorkScopesSummary
                    scopes={scopes}
                    job={job}
                    hoveredBlock={hoveredBlock}
                    onBlockHover={setHoveredBlock}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Job;
